/**
 * Sticky Trial Banner — persistent across the app while user is on trial.
 *
 * Behavior:
 *   • Shows ONLY when subscription_status === "trialing".
 *   • Copy morphs by urgency:
 *       > 7 days left  → soft nudge, dark-neutral bar
 *       3-7 days       → warmer amber bar
 *       ≤ 2 days       → urgent orange bar (matches brand accent)
 *       0 or expired   → red "your trial has ended" bar
 *   • Includes a CTA button to /app/billing (Pick a plan / Reactivate).
 *   • Dismissible per-session (localStorage flag) — but re-appears the next
 *     day so the reminder never truly disappears until user picks a plan.
 *   • Zero-config: reads `trial_ends_at` from the current user session.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowRight, X as XIcon, Warning } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
}

// Dismissal is per-day so the banner disappears for the rest of today but
// comes back tomorrow — trial urgency > sticky-banner fatigue.
function dismissKey() {
  const d = new Date();
  return `fpai_trial_banner_dismissed_${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

export default function TrialBanner() {
  const { user } = useAuth();
  const [dismissedAt, setDismissedAt] = useState(() =>
    typeof window !== "undefined" ? localStorage.getItem(dismissKey()) : null
  );

  const daysLeft = useMemo(() => daysUntil(user?.trial_ends_at), [user?.trial_ends_at]);
  const status = user?.subscription_status;

  // Only render for trialers. Paid + past_due handled elsewhere.
  if (!user) return null;
  if (status !== "trialing") return null;
  if (daysLeft === null) return null;
  if (dismissedAt) return null;

  const isExpired = daysLeft <= 0;
  const isUrgent = daysLeft > 0 && daysLeft <= 2;
  const isSoon = daysLeft > 2 && daysLeft <= 7;

  const styleClass = isExpired
    ? "bg-red-500/[0.08] border-red-500/30 text-red-100"
    : isUrgent
    ? "bg-[#FF5A1F]/[0.10] border-[#FF5A1F]/40 text-orange-100"
    : isSoon
    ? "bg-amber-400/[0.06] border-amber-400/25 text-amber-100"
    : "bg-white/[0.03] border-white/10 text-neutral-300";

  const ctaClass = isExpired
    ? "bg-red-500 hover:bg-red-400 text-white"
    : isUrgent
    ? "bg-[#FF5A1F] hover:bg-[#FF7038] text-black"
    : "bg-white/10 hover:bg-white/20 text-white border border-white/15";

  const headline = isExpired
    ? "Your free trial has ended"
    : daysLeft === 1
    ? "Your trial ends tomorrow"
    : daysLeft === 0
    ? "Your trial ends today"
    : `${daysLeft} days left on your free trial`;

  const subtext = isExpired
    ? "Reactivate to keep sending invoices, dispatching loads, and using AI features."
    : isUrgent
    ? "Pick a plan now so your loads, drivers, and AI settings stay live."
    : isSoon
    ? "No pressure — but locking in a plan takes 60 seconds and keeps everything running."
    : "Try every feature. Cancel anytime. Plans start at $79/mo.";

  const ctaLabel = isExpired ? "Reactivate account" : "Pick your plan";

  const handleDismiss = () => {
    try {
      localStorage.setItem(dismissKey(), new Date().toISOString());
    } catch {
      /* localStorage unavailable — silent */
    }
    setDismissedAt(new Date().toISOString());
  };

  return (
    <div
      data-testid="trial-banner"
      data-trial-urgency={isExpired ? "expired" : isUrgent ? "urgent" : isSoon ? "soon" : "calm"}
      className={`sticky top-0 z-20 border-b backdrop-blur-sm px-4 sm:px-6 py-2.5 flex items-center gap-3 text-sm ${styleClass}`}
    >
      {isExpired ? (
        <Warning size={18} weight="fill" className="shrink-0" />
      ) : (
        <Clock size={16} weight="bold" className="shrink-0 opacity-80" />
      )}
      <div className="flex items-baseline gap-2 flex-wrap min-w-0 flex-1">
        <span
          className="font-semibold tracking-tight"
          data-testid="trial-banner-headline"
        >
          {headline}
        </span>
        <span className="text-xs opacity-80 truncate hidden sm:inline">{subtext}</span>
      </div>
      <Link
        to="/app/billing"
        data-testid="trial-banner-cta"
        className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors shrink-0 ${ctaClass}`}
      >
        {ctaLabel} <ArrowRight size={12} weight="bold" />
      </Link>
      <button
        onClick={handleDismiss}
        data-testid="trial-banner-dismiss"
        title="Dismiss for today"
        className="p-1 rounded-md hover:bg-white/10 text-current opacity-60 hover:opacity-100 transition-opacity shrink-0"
      >
        <XIcon size={14} weight="bold" />
      </button>
    </div>
  );
}
